import { useMemo } from "react";

/* Kurzschlüssel über alle gesetzten Filter — Abhängigkeit der
   Auswahllisten. */
const signaturVon = (w) => JSON.stringify([w.typ, w.land, w.region, w.winzer, w.rebsorte,
  w.reife, w.lager, w.jahrgang, w.favorit, w.label]);
import { alsWahl, passtWahl, passtWahlMit } from "../kern/filterwahl.js";
import { eindeutigeWerte, flaschenVonWein, gesamtFlaschen, hatRebsorte, rebsortenListe, regionZuWein, reifeVonJahrgang, winzerZuWein } from "../kern/fachlogik.js";
import { jahrLabel, sortDe } from "../kern/format.js";
import { TYPEN } from "../kern/konstanten.js";
import { useEntprellt, usePersistentState } from "./hooks.jsx";
import { MehrfachAuswahl } from "./mehrfachauswahl.jsx";
import { Auswahl } from "./primitive.jsx";
import { t } from "../i18n/index.js";
import { landName } from "../i18n/laender.js";

/* ================================================================
   Gemeinsame Weinfilterung für Keller und Statistik.

   Beide Seiten teilen sich Zustand, Auswahllisten und Prüfregel, damit
   ein Filter überall dasselbe bedeutet. Der `praefix` trennt die
   gespeicherten Werte je Seite — so bleibt eine Filterung im Keller
   von der in der Statistik unberührt.
   ================================================================ */

/* `nurBestand` grenzt die Grundmenge ein, aus der die Auswahllisten
   gefüllt werden. Ohne das bot der Filter Lagerorte, Jahrgänge und
   Rebsorten an, die es in der gezeigten Liste gar nicht gibt — man
   wählte etwas aus und bekam eine leere Tabelle.

   Bewusst nur die Grundmenge, nicht die übrigen gesetzten Filter:
   Sonst schrumpfen die Listen gegenseitig, und wer erst Region und
   dann Land wählen will, findet sein Land nicht mehr. */
function useWeinFilter(db, praefix, vorgabe = null, nurBestand = false) {
  const [suche, setSuche] = usePersistentState(`${praefix}.suche`, "");
  /* Das Eingabefeld bekommt `suche` und reagiert sofort; gefiltert
     wird mit `sucheAktiv`, kurz danach. Siehe useEntprellt. */
  const sucheAktiv = useEntprellt(suche, 150);
  /* Alle Merkmale außer „Lieblingswein" sind Mehrfachwahlen: eine
     Liste gewählter Werte, leer heißt alle (kern/filterwahl.js).
     „Lieblingswein" ist ein Ja/Nein und bleibt eine Einfachwahl. */
  const [typ, setTyp] = usePersistentState(`${praefix}.typFilter`, []);
  const [region, setRegion] = usePersistentState(`${praefix}.regionFilter`, alsWahl(vorgabe?.regionId));
  const [winzer, setWinzer] = usePersistentState(`${praefix}.winzerFilter`, alsWahl(vorgabe?.winzerId));
  const [land, setLand] = usePersistentState(`${praefix}.landFilter`, []);
  const [reife, setReife] = usePersistentState(`${praefix}.reifeFilter`, []);
  const [lager, setLager] = usePersistentState(`${praefix}.lagerFilter`, []);
  const [rebsorte, setRebsorte] = usePersistentState(`${praefix}.rebsorteFilter`, []);
  const [jahrgang, setJahrgang] = usePersistentState(`${praefix}.jahrgangFilter`, []);
  const [favorit, setFavorit] = usePersistentState(`${praefix}.favoritFilter`, "");
  const [label, setLabel] = usePersistentState(`${praefix}.labelFilter`, []);

  /* ── Auswahllisten ────────────────────────────────────────────

     Die Optionen eines Filters ergeben sich aus den Weinen, die ALLE
     ÜBRIGEN Filter passieren — der Filter selbst wird ausgeklammert.
     Mit „Land: Deutschland“ stehen bei Region nur noch deutsche
     Regionen; die Länderliste bleibt aber vollständig, sonst käme man
     aus einer einmal getroffenen Wahl nicht mehr heraus — und könnte
     kein zweites Land dazunehmen.

     Der Bestandsschalter wirkt als Grundmenge auf alle Listen.
     Katalogweine haben nie Flaschen und bleiben deshalb drin. */
  const grund = useMemo(
    () => (nurBestand ? db.weine.filter((w) => w.nurKatalog || flaschenVonWein(w) > 0) : db.weine),
    [db.weine, nurBestand]);

  const werte = { typ, land, region, winzer, rebsorte, reife, lager, jahrgang, favorit, label };

  /* Prüft ein (Wein, Jahrgang)-Paar; `ausser` nennt ein Merkmal, das
     übersprungen wird (für die Auswahllisten). `jg` darf null sein —
     ein Wein ohne Jahrgänge fällt dann bei jedem Jahrgangsmerkmal
     heraus, sobald es gesetzt ist. */
  const passtOhne = (wein, jg, wz, rg, ausser) => {
    if (ausser !== "typ" && !passtWahl(typ, wein.typ)) return false;
    if (ausser !== "land" && !passtWahl(land, rg ? (rg.land || "Ohne Land") : "Ohne Land")) return false;
    if (ausser !== "region" && !passtWahl(region, rg ? rg.id : null)) return false;
    if (ausser !== "winzer" && !passtWahl(winzer, wz ? wz.id : null)) return false;
    if (ausser !== "rebsorte" && !passtWahlMit(rebsorte, (r) => hatRebsorte(wein, r))) return false;
    if (ausser !== "reife" && !passtWahl(reife, jg ? reifeVonJahrgang(jg) : null)) return false;
    if (ausser !== "lager" && !passtWahl(lager, jg ? (jg.lagerort || "Ohne Lagerort") : null)) return false;
    if (ausser !== "jahrgang" && !passtWahl(jahrgang, jg ? jg.jahr : null)) return false;
    if (ausser !== "favorit" && favorit && !wein.favorit) return false;
    if (ausser !== "label" && !passtWahl(label, wein.label || "")) return false;
    return true;
  };

  /* (Wein, Jahrgang)-Paare der Grundmenge, einmal aufgebaut. Ein Wein
     ohne Jahrgänge zählt mit `jg: null` — er soll bei Land, Winzer und
     Typ vorkommen, bei Jahrgang und Lagerort nicht. */
  const paare = useMemo(() => {
    const raus = [];
    for (const wein of grund) {
      const wz = winzerZuWein(db, wein);
      const rg = regionZuWein(db, wein);
      const jgs = (nurBestand && !wein.nurKatalog)
        ? wein.jahrgaenge.filter((jg) => gesamtFlaschen(jg) > 0)
        : wein.jahrgaenge;
      if (jgs.length === 0) raus.push({ wein, jg: null, wz, rg });
      else for (const jg of jgs) raus.push({ wein, jg, wz, rg });
    }
    return raus;
  }, [grund, db, nurBestand]);

  const sig = signaturVon(werte) + (nurBestand ? "|1" : "|0");

  /* Sammelt die Werte eines Merkmals aus den passenden Paaren. */
  const sammle = (ausser, hole) => {
    const set = new Set();
    for (const e of paare) {
      if (!passtOhne(e.wein, e.jg, e.wz, e.rg, ausser)) continue;
      hole(set, e);
    }
    return set;
  };
  /* eslint-disable react-hooks/exhaustive-deps */
  const sortiert = (set) => [...set].sort((a, b) => String(a).localeCompare(String(b), "de"));

  const rebsorten = useMemo(() =>
    sortiert(sammle("rebsorte", (m, e) => rebsortenListe([e.wein]).forEach((r) => m.add(r)))), [paare, sig]);
  const labels = useMemo(() =>
    sortiert(sammle("label", (m, e) => { if (e.wein.label) m.add(e.wein.label); })), [paare, sig]);
  const laender = useMemo(() =>
    sortiert(sammle("land", (m, e) => m.add(e.rg ? (e.rg.land || "Ohne Land") : "Ohne Land"))), [paare, sig]);
  const lagerorte = useMemo(() =>
    sortiert(sammle("lager", (m, e) => { if (e.jg && e.jg.lagerort) m.add(e.jg.lagerort); })), [paare, sig]);
  const jahrgaenge = useMemo(() =>
    [...sammle("jahrgang", (m, e) => { if (e.jg && e.jg.jahr != null) m.add(e.jg.jahr); })]
      .sort((a, b) => b - a), [paare, sig]);
  const winzerIds = useMemo(() => sammle("winzer", (m, e) => { if (e.wz) m.add(e.wz.id); }), [paare, sig]);
  const regionIds = useMemo(() => sammle("region", (m, e) => { if (e.rg) m.add(e.rg.id); }), [paare, sig]);
  const typen = useMemo(() => sammle("typ", (m, e) => { if (e.wein.typ) m.add(e.wein.typ); }), [paare, sig]);
  const reifen = useMemo(() =>
    sammle("reife", (m, e) => { if (e.jg) m.add(reifeVonJahrgang(e.jg)); }), [paare, sig]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const aktiv = !!(typ.length || land.length || region.length || winzer.length || rebsorte.length
    || reife.length || lager.length || jahrgang.length || favorit || label.length);

  const reset = () => {
    setTyp([]); setLand([]); setRegion([]); setWinzer([]); setRebsorte([]);
    setReife([]); setLager([]); setJahrgang([]); setFavorit(""); setLabel([]);
  };

  /* Prüft einen einzelnen Jahrgang eines Weins. Winzer und Region
     werden übergeben, damit sie nicht je Zeile neu gesucht werden. */
  const passt = (wein, jg, winzerObj, regionObj) => {
    if (!passtOhne(wein, jg, winzerObj, regionObj, null)) return false;
    const q = sucheAktiv.trim().toLowerCase();
    if (q) {
      const heu = `${wein.name} ${winzerObj ? winzerObj.name : ""} ${regionObj ? regionObj.name : ""} ${wein.rebsorten || ""} ${wein.label || ""} ${jg.jahr}`.toLowerCase();
      if (!heu.includes(q)) return false;
    }
    return true;
  };

  return {
    suche, setSuche, sucheAktiv,
    werte: { typ, land, region, winzer, rebsorte, reife, lager, jahrgang, favorit, label },
    setzer: { setTyp, setLand, setRegion, setWinzer, setRebsorte, setReife, setLager, setJahrgang, setFavorit, setLabel },
    optionen: { rebsorten, laender, lagerorte, jahrgaenge, labels, winzerIds, regionIds, typen, reifen },
    aktiv, reset, passt,
    /* Für useMemo-Abhängigkeiten: ändert sich, sobald ein Filter kippt. */
    signatur: [sucheAktiv, signaturVon(werte)].join("|"),
  };
}

const REIFEN = [["jung", "Noch nicht trinkreif"], ["reif", "Trinkreif"], ["ueber", "Überfällig"]];
const landLabel = (l) => (l === "Ohne Land" ? t("Ohne Land") : landName(l));

/* Die Auswahlfelder — identisch auf beiden Seiten. */
function FilterFelder({ db, f }) {
  const { werte: v, setzer: s, optionen: o } = f;
  const regionName = (id) => (db.regions.find((r) => r.id === id) || {}).name || id;
  const winzerName = (id) => (db.winzer.find((w) => w.id === id) || {}).name || id;
  return (
    <>
      <Auswahl value={v.favorit} onChange={(e) => s.setFavorit(e.target.value)} aria-label={t("Nach Lieblingsweinen filtern")}>
        <option value="">{t("Alle Weine")}</option>
        <option value="1">{t("Nur Lieblingsweine")}</option>
      </Auswahl>
      <MehrfachAuswahl wahl={v.typ} setWahl={s.setTyp} alleText={t("Alle Typen")} beschriftung={t("Nach Weintyp filtern")}
        optionen={Object.entries(TYPEN).filter(([k]) => o.typen.has(k)).map(([k, typ]) => ({ wert: k, label: t(typ.label) }))}
        beschrifte={(k) => (TYPEN[k] ? t(TYPEN[k].label) : k)} />
      <MehrfachAuswahl wahl={v.jahrgang} setWahl={s.setJahrgang} alleText={t("Alle Jahrgänge")} beschriftung={t("Nach Jahrgang filtern")}
        optionen={o.jahrgaenge.map((j) => ({ wert: String(j), label: jahrLabel(j) }))}
        beschrifte={(j) => jahrLabel(Number(j))} />
      <MehrfachAuswahl wahl={v.land} setWahl={s.setLand} alleText={t("Alle Länder")} beschriftung={t("Nach Land filtern")}
        optionen={o.laender.map((l) => ({ wert: l, label: landLabel(l) }))} beschrifte={landLabel} />
      <MehrfachAuswahl wahl={v.region} setWahl={s.setRegion} alleText={t("Alle Regionen")} beschriftung={t("Nach Region filtern")}
        optionen={[...db.regions].filter((r) => o.regionIds.has(r.id)).sort(sortDe).map((r) => ({ wert: r.id, label: r.name }))}
        beschrifte={regionName} />
      <MehrfachAuswahl wahl={v.winzer} setWahl={s.setWinzer} alleText={t("Alle Winzer")} beschriftung={t("Nach Winzer filtern")}
        optionen={[...db.winzer].filter((w) => o.winzerIds.has(w.id)).sort(sortDe).map((w) => ({ wert: w.id, label: w.name }))}
        beschrifte={winzerName} />
      <MehrfachAuswahl wahl={v.rebsorte} setWahl={s.setRebsorte} alleText={t("Alle Rebsorten")} beschriftung={t("Nach Rebsorte filtern")}
        optionen={o.rebsorten.map((r) => ({ wert: r, label: r }))} />
      <MehrfachAuswahl wahl={v.reife} setWahl={s.setReife} alleText={t("Jede Trinkreife")} beschriftung={t("Nach Trinkreife filtern")}
        optionen={REIFEN.filter(([k]) => o.reifen.has(k)).map(([k, lbl]) => ({ wert: k, label: t(lbl) }))}
        beschrifte={(k) => t((REIFEN.find(([x]) => x === k) || [k, k])[1])} />
      {(o.labels.length > 0 || v.label.length > 0) && (
        <MehrfachAuswahl wahl={v.label} setWahl={s.setLabel} alleText={t("Alle Labels")} beschriftung={t("Nach Label filtern")}
          optionen={o.labels.map((l) => ({ wert: l, label: l }))} />
      )}
      {(o.lagerorte.length > 0 || v.lager.length > 0) && (
        <MehrfachAuswahl wahl={v.lager} setWahl={s.setLager} alleText={t("Alle Lagerorte")} beschriftung={t("Nach Lagerort filtern")}
          optionen={o.lagerorte.map((l) => ({ wert: l, label: l }))}
          beschrifte={(l) => (l === "Ohne Lagerort" ? t("Ohne Lagerort") : l)} />
      )}
    </>
  );
}

/* ----------------------------------------------------------------
   Gefiltertes Kellerbuch für die Statistik.

   Die Statistik rechnet vollständig aus dem Kellerbuch. Statt jede
   Auswertung einzeln zu filtern, wird hier einmal ein reduziertes
   Kellerbuch gebaut — Weine, Jahrgänge, Entnahmen und Bewegungen.
   Damit folgen Kennzahlen, Balken, Weltkarte und Verlauf demselben
   Filter, ohne dass die Auswertungen davon wissen müssen.

   Bewusst NICHT gefiltert wird nach Bestand: sonst verschwänden
   ausgetrunkene Weine aus „Getrunken“.
   ---------------------------------------------------------------- */
function filtereDb(db, f) {
  if (!f.aktiv && !(f.sucheAktiv ?? f.suche).trim()) return db;

  const weine = [];
  const erlaubteJgIds = new Set();
  const erlaubteJahre = new Map();   /* weinId -> Set(Jahrgangsjahr) */

  for (const wein of db.weine) {
    const winzerObj = winzerZuWein(db, wein);
    const regionObj = regionZuWein(db, wein);
    const jgs = wein.jahrgaenge.filter((jg) => f.passt(wein, jg, winzerObj, regionObj));
    if (jgs.length === 0) continue;
    weine.push({ ...wein, jahrgaenge: jgs });
    const jahre = new Set();
    for (const jg of jgs) { erlaubteJgIds.add(jg.id); jahre.add(jg.jahr); }
    erlaubteJahre.set(wein.id, jahre);
  }

  /* Entnahmen und Bewegungen zeigen auf einen Jahrgang — entweder über
     jgId oder (bei älteren Einträgen) über das Jahr. */
  const eintragPasst = (e) => {
    if (!e || !e.weinId) return false;
    const jahre = erlaubteJahre.get(e.weinId);
    if (!jahre) return false;
    if (e.jgId) return erlaubteJgIds.has(e.jgId);
    if (e.jahr != null) return jahre.has(e.jahr);
    return true;
  };

  return {
    ...db,
    weine,
    entnahmen: (db.entnahmen || []).filter(eintragPasst),
    bewegungen: (db.bewegungen || []).filter(eintragPasst),
  };
}

export { FilterFelder, filtereDb, useWeinFilter };
